import React, { useEffect, useMemo, useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import { GridColDef } from '@mui/x-data-grid';
import { PageHeader } from './layout/PageHeader';
import { StatCard } from './layout/StatCard';
import { TableCard } from './layout/TableCard';
import { PageLoader } from './PageLoader';
import { objectivesApi, keyResultsApi, ObjectiveListItem, KeyResultListItem, portfoliosApi, PortfolioListItem, ppmProjectsApi, PpmProjectListItem } from '../api/ppm';
import { usersApi, DbUser } from '../api/client';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { canEditPage } from '../utils/rbac';
import { ResizableDataGrid } from './grid/ResizableDataGrid';
import { ChartCard } from './charts/ChartCard';
import { NexusBarChart, NexusDonut } from './charts/NexusCharts';
import { tokens, chartColor } from '../theme/tokens';

function statusColor(s?: string | null) {
  const v = String(s || '').toUpperCase();
  if (v === 'COMPLETED') return 'success';
  if (v === 'AT_RISK') return 'warning';
  if (v === 'OFF_TRACK') return 'error';
  if (v === 'ON_TRACK') return 'primary';
  return 'default';
}

const OkrsPage: React.FC = () => {
  const { dbUser } = useCurrentUser();
  const canEdit = canEditPage(dbUser, 'okrs');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [portfolios, setPortfolios] = useState<PortfolioListItem[]>([]);
  const [projects, setProjects] = useState<PpmProjectListItem[]>([]);
  const [users, setUsers] = useState<DbUser[]>([]);
  const [me, setMe] = useState<DbUser | null>(null);

  // Top-level Portfolio/Status filters were removed (they were confusing and appeared non-functional).

  const [objectives, setObjectives] = useState<ObjectiveListItem[]>([]);
  const [okrStats, setOkrStats] = useState<{ total: number; byStatus: Record<string, number>; averageProgress: number } | null>(null);
  const [krStats, setKrStats] = useState<{ total: number; averageProgress: number } | null>(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState<ObjectiveListItem | null>(null);
  const [objectiveKrs, setObjectiveKrs] = useState<KeyResultListItem[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [krActualDraft, setKrActualDraft] = useState<Record<string, string>>({});
  const [savingKrActualId, setSavingKrActualId] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<ObjectiveListItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [objectiveForm, setObjectiveForm] = useState({
    title: '',
    description: '',
    ownerId: '',
    portfolioId: '',
    projectId: '',
    status: 'ACTIVE',
    startDate: '',
    endDate: '',
    unit: '%',
    targetValue: '100',
    currentValue: '0',
  });

  const [krOpen, setKrOpen] = useState(false);
  const [savingKr, setSavingKr] = useState(false);
  const [krForm, setKrForm] = useState({
    title: '',
    description: '',
    unit: '%',
    direction: 'increase' as 'increase' | 'decrease',
    startValue: '',
    targetValue: '100',
    currentValue: '0',
    startDate: '',
    endDate: '',
  });

  const calcKrPercent = (opts: { direction: 'increase' | 'decrease'; startValue?: number | null; currentValue: number; targetValue: number }) => {
    const current = Number(opts.currentValue || 0);
    const target = Number(opts.targetValue || 0);
    if (opts.direction === 'decrease') {
      const start = opts.startValue == null ? current : Number(opts.startValue);
      if (!isFinite(start) || !isFinite(target) || start === target) return 100;
      if (start <= target || target === 0) return target > 0 ? Math.round(Math.min((current / target) * 100, 100)) : 0;
      const pct = ((start - current) / (start - target)) * 100;
      return Math.round(Math.max(0, Math.min(100, pct)));
    }
    return target > 0 ? Math.round(Math.min((current / target) * 100, 100)) : 0;
  };

  const [topPanel, setTopPanel] = useState<null | 'total' | 'onTrack' | 'atRisk' | 'avgProgress'>(null);
  const [showAllPanel, setShowAllPanel] = useState(false);

  const userById = useMemo(() => {
    const m = new Map<string, DbUser>();
    users.forEach((u) => m.set(u.id, u));
    return m;
  }, [users]);

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const [pRes, projRes, uRes, oRes, sRes, kRes, meRes] = await Promise.all([
        portfoliosApi.getAll(),
        ppmProjectsApi.getAll({ source: 'internal' }),
        usersApi.getAll({ scopePage: 'okrs' }),
        objectivesApi.getAll(),
        objectivesApi.getStats(),
        keyResultsApi.getStats(),
        usersApi.me().catch(() => ({ data: null } as any)),
      ]);

      setPortfolios((pRes.data || []) as PortfolioListItem[]);
      setProjects(projRes.data || []);
      setUsers(uRes.data || []);
      setMe((meRes as any)?.data || null);
      // Derive objective progress from Key Results average completion so progress is consistent everywhere.
      const computed = (oRes.data || []).map((o: any) => {
        const krs = Array.isArray(o?.KeyResults) ? o.KeyResults : [];
        if (!krs.length) return { ...o, percentComplete: 0 };
        // Use average progress of all KeyResults, not just "done count"
        const totalProgress = krs.reduce((sum: number, kr: any) => sum + Math.max(0, Math.min(100, Number(kr?.percentComplete || 0))), 0);
        const pct = Math.round(totalProgress / krs.length);
        return { ...o, percentComplete: pct };
      });
      setObjectives(computed as any);
      setOkrStats(sRes.data || null);
      setKrStats(kRes.data || null);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load OKRs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (row: ObjectiveListItem) => {
    setSelectedObjective(row);
    setDetailOpen(true);
    try {
      setLoadingDetail(true);
      const res = await keyResultsApi.getAll({ objectiveId: row.id });
      setObjectiveKrs(res.data || []);
      // Recompute objective % based on KR average completion so the detail view and list are always consistent.
      const krs = res.data || [];
      if (!krs.length) {
        setSelectedObjective((prev) => (prev ? { ...prev, percentComplete: 0 } : prev));
        setObjectives((prev) => prev.map((o: any) => (String(o.id) === String(row.id) ? { ...o, percentComplete: 0 } : o)));
        return;
      }
      const totalProgress = krs.reduce((sum: number, kr: any) => sum + Math.max(0, Math.min(100, Number(kr?.percentComplete || 0))), 0);
      const pct = Math.round(totalProgress / krs.length);
      setSelectedObjective((prev) => (prev ? { ...prev, percentComplete: pct } : prev));
      setObjectives((prev) => prev.map((o: any) => (String(o.id) === String(row.id) ? { ...o, percentComplete: pct } : o)));
      const draft: Record<string, string> = {};
      (res.data || []).forEach((kr) => {
        draft[kr.id] = String((kr as any).currentValue ?? '0');
      });
      setKrActualDraft(draft);
    } catch {
      setObjectiveKrs([]);
    } finally {
      setLoadingDetail(false);
    }
  };

  const saveKrActual = async (kr: KeyResultListItem) => {
    const raw = krActualDraft[kr.id];
    const v = Number(raw);
    if (!Number.isFinite(v) || v < 0) {
      setError('Key result actual must be a valid non-negative number.');
      return;
    }
    try {
      setSavingKrActualId(kr.id);
      setError(null);
      await keyResultsApi.updateProgress(kr.id, v);
      // refresh details + top stats/objectives rollups
      if (selectedObjective?.id) {
        const [krsRes, objRes] = await Promise.all([
          keyResultsApi.getAll({ objectiveId: selectedObjective.id }),
          objectivesApi.getAll(),
        ]);
        setObjectiveKrs(krsRes.data || []);
        const computed = (objRes.data || []).map((o: any) => {
          const krs = Array.isArray(o?.KeyResults) ? o.KeyResults : [];
          if (!krs.length) return { ...o, percentComplete: 0 };
          const totalProgress = krs.reduce((sum: number, kr: any) => sum + Math.max(0, Math.min(100, Number(kr?.percentComplete || 0))), 0);
          const pct = Math.round(totalProgress / krs.length);
          return { ...o, percentComplete: pct };
        });
        setObjectives(computed as any);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to update key result actual');
    } finally {
      setSavingKrActualId(null);
    }
  };

  const openNewObjective = () => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 2, 0));
    setEditing(null);
    setObjectiveForm({
      title: '',
      description: '',
      ownerId: me?.id || '',
      portfolioId: portfolios[0]?.id || '',
      projectId: '',
      status: 'ACTIVE',
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10),
      unit: '%',
      targetValue: '100',
      currentValue: '0',
    });
    setEditOpen(true);
  };

  const openEditObjective = (o: ObjectiveListItem) => {
    setEditing(o);
    setObjectiveForm({
      title: o.title || '',
      description: o.description || '',
      ownerId: o.ownerId || o.Owner?.id || me?.id || '',
      portfolioId: o.portfolioId || '',
      projectId: o.projectId || '',
      status: String(o.status || 'ACTIVE'),
      startDate: (o as any).startDate ? String((o as any).startDate).slice(0, 10) : '',
      endDate: (o as any).endDate ? String((o as any).endDate).slice(0, 10) : '',
      unit: (o as any).unit || '%',
      targetValue: String((o as any).targetValue ?? ''),
      currentValue: String((o as any).currentValue ?? ''),
    });
    setEditOpen(true);
  };

  const saveObjective = async () => {
    try {
      setSaving(true);
      const payload: any = {
        title: objectiveForm.title,
        description: objectiveForm.description || undefined,
        // Owner is forced server-side to the authenticated user.
        portfolioId: objectiveForm.portfolioId || null,
        projectId: objectiveForm.projectId || null,
        status: objectiveForm.status,
        startDate: objectiveForm.startDate ? new Date(`${objectiveForm.startDate}T00:00:00.000Z`) : new Date(),
        endDate: objectiveForm.endDate ? new Date(`${objectiveForm.endDate}T00:00:00.000Z`) : new Date(),
        unit: objectiveForm.unit || '%',
        targetValue: objectiveForm.targetValue ? Number(objectiveForm.targetValue) : null,
        currentValue: objectiveForm.currentValue ? Number(objectiveForm.currentValue) : null,
        percentComplete:
          objectiveForm.targetValue && Number(objectiveForm.targetValue) > 0
            ? Math.round((Math.min(Number(objectiveForm.currentValue) || 0, Number(objectiveForm.targetValue) || 0) / Number(objectiveForm.targetValue)) * 100)
            : 0,
      };

      if (editing?.id) {
        await objectivesApi.update(editing.id, payload);
      } else {
        await objectivesApi.create(payload);
      }
      setEditOpen(false);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteObjective = async (id: string) => {
    try {
      await objectivesApi.remove(id);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Delete failed');
    }
  };

  const openNewKr = () => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 2, 0));
    setKrForm({
      title: '',
      description: '',
      unit: objectiveForm.unit || '%',
      direction: 'increase',
      startValue: '',
      targetValue: objectiveForm.targetValue || '100',
      currentValue: '0',
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10),
    });
    setKrOpen(true);
  };

  const createKr = async () => {
    if (!selectedObjective?.id) return;
    try {
      setSavingKr(true);
      const direction = (krForm as any).direction || 'increase';
      const currentValue = Number((krForm as any).currentValue) || 0;
      const targetValue = Number((krForm as any).targetValue) || 0;
      const startValue =
        direction === 'decrease'
          ? ((krForm as any).startValue?.trim() ? Number((krForm as any).startValue) : currentValue)
          : undefined;
      const percentComplete = calcKrPercent({ direction, startValue: startValue ?? null, currentValue, targetValue });
      await keyResultsApi.create({
        objectiveId: selectedObjective.id,
        title: krForm.title,
        description: krForm.description || undefined,
        unit: krForm.unit || '%',
        direction,
        startValue,
        targetValue,
        currentValue,
        startDate: krForm.startDate ? new Date(`${krForm.startDate}T00:00:00.000Z`) : new Date(),
        endDate: krForm.endDate ? new Date(`${krForm.endDate}T00:00:00.000Z`) : new Date(),
        percentComplete,
      });
      setKrOpen(false);
      await openDetail(selectedObjective);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to add key result');
    } finally {
      setSavingKr(false);
    }
  };

  const deleteKr = async (id: string) => {
    try {
      await keyResultsApi.remove(id);
      if (selectedObjective) await openDetail(selectedObjective);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to delete key result');
    }
  };

  const cols: GridColDef[] = [
    { field: 'title', headerName: 'Objective', flex: 1.4, minWidth: 280 },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => (
        <Chip size="small" variant="outlined" label={String(params.value || '—')} color={statusColor(String(params.value || '')) as any} sx={{ height: 22, fontWeight: 650 }} />
      ),
    },
    {
      field: 'percentComplete',
      headerName: 'Progress',
      minWidth: 220,
      flex: 0.9,
      renderCell: (params) => {
        const v = Math.max(0, Math.min(100, Number(params.value) || 0));
        return (
          <Box sx={{ width: '100%', pr: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 650 }}>
                {Math.round(v)}%
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650 }}>
                {v >= 100 ? 'Done' : v >= 70 ? 'On track' : v >= 40 ? 'Watch' : 'At risk'}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={v}
              sx={{ height: 8, borderRadius: 999, backgroundColor: tokens.color.neutralSoft }}
            />
          </Box>
        );
      },
    },
    {
      field: 'ownerId',
      headerName: 'Owner',
      width: 220,
      valueGetter: (params) => {
        const r: any = params.row;
        const v = params.value;
        return r?.Owner?.displayName || r?.Owner?.email || userById.get(String(v))?.displayName || userById.get(String(v))?.email || '—';
      },
    },
    {
      field: 'projectId',
      headerName: 'Project',
      width: 220,
      valueGetter: (params) => {
        const r: any = params.row;
        const v = params.value;
        return r?.Project?.name || projects.find((p) => p.id === String(v))?.name || '—';
      },
    },
    ...(canEdit
      ? ([
          {
            field: 'actions',
            headerName: '',
            width: 140,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <Stack direction="row" spacing={0.5}>
                <Button size="small" variant="text" onClick={() => openEditObjective(params.row as any)} sx={{ fontWeight: 650 }}>
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="text"
                  onClick={() => deleteObjective(String((params.row as any).id))}
                  sx={{ fontWeight: 650 }}
                >
                  Delete
                </Button>
              </Stack>
            ),
          },
        ] as GridColDef[])
      : []),
  ];

  const totals = useMemo(() => {
    const total = okrStats?.total || objectives.length;
    const by = okrStats?.byStatus || {};
    const atRisk = (by['AT_RISK'] || 0) + (by['OFF_TRACK'] || 0);
    const onTrack = by['ON_TRACK'] || 0;
    const avg = okrStats?.averageProgress ?? (objectives.reduce((s, o) => s + (Number(o.percentComplete) || 0), 0) / Math.max(1, objectives.length));
    return { total, atRisk, onTrack, avg, krTotal: krStats?.total || 0, krAvg: krStats?.averageProgress || 0 };
  }, [okrStats, krStats, objectives]);

  const statusMix = useMemo(() => {
    const map = new Map<string, number>();
    (objectives || []).forEach((o: any) => {
      const s = String(o.status || 'UNSPECIFIED').toUpperCase();
      map.set(s, (map.get(s) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [objectives]);

  const progressBuckets = useMemo(() => {
    const buckets = [
      { name: '0–20%', value: 0 },
      { name: '20–40%', value: 0 },
      { name: '40–60%', value: 0 },
      { name: '60–80%', value: 0 },
      { name: '80–100%', value: 0 },
    ];
    (objectives || []).forEach((o: any) => {
      const pct = Math.max(0, Math.min(100, Number(o.percentComplete) || 0));
      if (pct < 20) buckets[0].value += 1;
      else if (pct < 40) buckets[1].value += 1;
      else if (pct < 60) buckets[2].value += 1;
      else if (pct < 80) buckets[3].value += 1;
      else buckets[4].value += 1;
    });
    return buckets;
  }, [objectives]);

  return (
    <PageLoader loading={loading}>
      <Container maxWidth="xl" sx={{ pt: 1, pb: 3 }}>
      <PageHeader
        title="OKR Management"
        subtitle="Objectives, key results, progress rollups, and contributions."
        actions={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" variant="outlined" onClick={refresh} sx={{ borderRadius: 2 }}>
              Refresh
            </Button>
            {canEdit ? (
              <Button
                size="small"
                variant="contained"
                onClick={openNewObjective}
                sx={{ borderRadius: 2 }}
              >
                New objective
              </Button>
            ) : null}
          </Box>
        }
      />

      {error ? (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Objectives"
            value={totals.total}
            subtitle="Tracked objectives"
            accent={tokens.color.accent}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'total' ? null : 'total'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="On track"
            value={totals.onTrack}
            subtitle="Status = ON_TRACK"
            accent={tokens.color.success}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'onTrack' ? null : 'onTrack'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="At risk"
            value={totals.atRisk}
            subtitle="AT_RISK + OFF_TRACK"
            accent={tokens.color.warning}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'atRisk' ? null : 'atRisk'));
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Avg progress"
            value={`${Math.round(totals.avg)}%`}
            subtitle="Objective rollup"
            accent={tokens.color.accent}
            onClick={() => {
              setShowAllPanel(false);
              setTopPanel((p) => (p === 'avgProgress' ? null : 'avgProgress'));
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Collapse in={!!topPanel} unmountOnExit>
            <TableCard
              title={topPanel === 'total' ? 'Objectives' : topPanel === 'onTrack' ? 'On track objectives' : topPanel === 'atRisk' ? 'At risk objectives' : 'Lowest progress objectives'}
              actions={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button size="small" variant="outlined" onClick={() => setShowAllPanel((s) => !s)} sx={{ borderRadius: 2 }}>
                    {showAllPanel ? 'Show less' : 'Show all'}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setTopPanel(null)} sx={{ borderRadius: 2 }}>
                    Close
                  </Button>
                </Stack>
              }
            >
              {(() => {
                const list =
                  topPanel === 'total'
                    ? objectives
                    : topPanel === 'onTrack'
                      ? objectives.filter((o) => String(o.status || '').toUpperCase() === 'ON_TRACK')
                      : topPanel === 'atRisk'
                        ? objectives.filter((o) => ['AT_RISK', 'OFF_TRACK'].includes(String(o.status || '').toUpperCase()))
                        : objectives.slice().sort((a, b) => (Number(a.percentComplete) || 0) - (Number(b.percentComplete) || 0));

                if (!list.length) {
                  return (
                    <Alert severity="info" variant="outlined">
                      No results.
                    </Alert>
                  );
                }

                return (
                  <Stack spacing={1}>
                    {list.slice(0, showAllPanel ? 1000 : 5).map((o) => {
                      const pct = Math.max(0, Math.min(100, Number((o as any).percentComplete) || 0));
                      const owner = (o as any).Owner?.displayName || (o as any).Owner?.email || '—';
                      return (
                        <Box
                          key={o.id}
                          sx={{ p: 1, borderRadius: 2, border: `1px solid ${tokens.color.border}`, cursor: 'pointer', '&:hover': { backgroundColor: tokens.color.surfaceMuted } }}
                          onClick={() => openDetail(o as any)}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography sx={{ fontWeight: 650, minWidth: 0 }} noWrap title={o.title || ''}>
                              {o.title || 'Untitled objective'}
                            </Typography>
                            <Chip size="small" label={`${Math.round(pct)}%`} variant="outlined" sx={{ height: 22, fontWeight: 650 }} />
                          </Box>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {o.status ? String(o.status) : '—'} • owner {owner}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                );
              })()}
            </TableCard>
          </Collapse>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <ChartCard title="Objective status mix" subtitle="All loaded objectives" height={260}>
            <NexusDonut
              height={200}
              centerLabel="Total"
              centerValue={String(totals.total)}
              data={statusMix.map((s, i) => {
                const key = String(s.name).toUpperCase();
                const color =
                  key === 'COMPLETED' || key === 'ON_TRACK'
                    ? tokens.color.success
                    : key === 'AT_RISK'
                      ? tokens.color.warning
                      : key === 'OFF_TRACK'
                        ? tokens.color.danger
                        : chartColor(i);
                return { name: s.name, value: s.value, color };
              })}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Progress distribution" subtitle="Objective % complete buckets" height={260}>
            <NexusBarChart
              data={progressBuckets}
              xKey="name"
              height={232}
              series={[{ key: 'value', name: 'Objectives', color: tokens.color.accent }]}
              valueFormatter={(v) => `${v}`}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Key results rollup" subtitle="Quick rollup from backend stats" height={260}>
            <NexusBarChart
              data={[
                { name: 'KR total', value: Number(totals.krTotal || 0) },
                { name: 'KR avg %', value: Math.round(Number(totals.krAvg || 0)) },
              ]}
              xKey="name"
              height={232}
              series={[{ key: 'value', name: 'Value', color: tokens.color.accent }]}
              valueFormatter={(v) => `${v}`}
            />
          </ChartCard>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableCard
              title="Objectives"
              subtitle="Click any objective for details and key results."
              actions={
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Key results: ${totals.krTotal} • Avg ${Math.round(totals.krAvg)}%`}
                  sx={{ height: 26, fontWeight: 650 }}
                />
              }
            >
              <div style={{ width: '100%', height: 620 }}>
                <ResizableDataGrid
                  storageKey="gridWidths.okrs.objectives"
                  enableDensityControls
                  rows={objectives}
                  columns={cols}
                  getRowId={(r) => r.id}
                  disableRowSelectionOnClick
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                  onRowClick={(p) => openDetail(p.row as any)}
                />
              </div>
            </TableCard>
          </Grid>
        </Grid>
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700 }}>Objective details</DialogTitle>
        <DialogContent>
          {selectedObjective ? (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={selectedObjective.status || '—'} variant="outlined" sx={{ fontWeight: 650 }} />
                <Chip size="small" label={`${Math.round(Number(selectedObjective.percentComplete) || 0)}%`} variant="outlined" sx={{ fontWeight: 650 }} />
                {selectedObjective.Owner?.displayName || selectedObjective.Owner?.email ? (
                  <Chip size="small" label={`Owner: ${selectedObjective.Owner.displayName || selectedObjective.Owner.email}`} variant="outlined" sx={{ fontWeight: 650 }} />
                ) : null}
                {selectedObjective.Project?.name ? (
                  <Chip size="small" label={`Project: ${selectedObjective.Project.name}`} variant="outlined" sx={{ fontWeight: 650 }} />
                ) : null}
              </Stack>

              <Typography sx={{ fontWeight: 700 }}>{selectedObjective.title}</Typography>
              {selectedObjective.description ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {selectedObjective.description}
                </Typography>
              ) : null}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Key Results
              </Typography>
              {canEdit ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Button size="small" variant="contained" onClick={openNewKr} sx={{ borderRadius: 2 }}>
                    Add key result
                  </Button>
                </Box>
              ) : null}
              {loadingDetail ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : objectiveKrs.length ? (
                <Stack spacing={1}>
                  {objectiveKrs.map((kr) => {
                    const v = Math.max(0, Math.min(100, Number(kr.percentComplete) || 0));
                    return (
                      <Box key={kr.id} sx={{ p: 1.25, border: `1px solid ${tokens.color.border}`, borderRadius: 2, backgroundColor: tokens.color.surface }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                          <Typography sx={{ fontWeight: 650, minWidth: 0 }} noWrap title={kr.title}>
                            {kr.title}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="caption" sx={{ fontWeight: 650, whiteSpace: 'nowrap' }}>
                              {Math.round(v)}%
                            </Typography>
                            {canEdit ? (
                              <Button size="small" color="error" variant="text" onClick={() => deleteKr(kr.id)} sx={{ fontWeight: 650 }}>
                                Delete
                              </Button>
                            ) : null}
                          </Stack>
                        </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 0.75 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650 }}>
                            {(() => {
                              const unit = String((kr as any).unit || '').trim();
                              const actual = (kr as any).currentValue ?? 0;
                              const target = (kr as any).targetValue ?? '—';
                              const dir = String((kr as any).direction || 'increase').toLowerCase();
                              const unitLabel = unit ? ` ${unit}` : '';
                              const dirLabel = dir === 'decrease' ? ' (lower is better)' : '';
                              return `Actual: ${actual}${unitLabel} • Target: ${target}${unitLabel}${dirLabel}`;
                            })()}
                          </Typography>
                          {canEdit ? (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
                              <TextField
                                size="small"
                                label="Actual"
                                value={krActualDraft[kr.id] ?? String((kr as any).currentValue ?? '0')}
                                onChange={(e) => setKrActualDraft((m) => ({ ...m, [kr.id]: e.target.value }))}
                                sx={{ width: 140 }}
                              />
                              <Button
                                size="small"
                                variant="contained"
                                disabled={savingKrActualId === kr.id}
                                onClick={() => saveKrActual(kr)}
                                sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
                              >
                                {savingKrActualId === kr.id ? 'Saving…' : 'Update'}
                              </Button>
                            </Stack>
                          ) : null}
                        </Stack>
                        <LinearProgress variant="determinate" value={v} sx={{ height: 8, borderRadius: 999, backgroundColor: tokens.color.neutralSoft }} />
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Alert severity="info" variant="outlined">
                  No key results found for this objective yet.
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Contributions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contributions tracking (linking Jira/PPM work items to OKRs) is wired for the data model, but we’ll surface it here next.
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button onClick={() => setDetailOpen(false)} variant="contained" sx={{ borderRadius: 2 }}>
                  Close
                </Button>
              </Box>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>{editing ? 'Edit objective' : 'New objective'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
            <Grid item xs={12}>
              <TextField label="Title" value={objectiveForm.title} onChange={(e) => setObjectiveForm((v) => ({ ...v, title: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={objectiveForm.description}
                onChange={(e) => setObjectiveForm((v) => ({ ...v, description: e.target.value }))}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Alert severity="info" variant="outlined">
                Owner is set automatically to <b>{me?.username || me?.email || 'you'}</b>.
              </Alert>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Status" value={objectiveForm.status} onChange={(e) => setObjectiveForm((v) => ({ ...v, status: e.target.value }))} fullWidth>
                <MenuItem value="DRAFT">DRAFT</MenuItem>
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="ON_TRACK">ON_TRACK</MenuItem>
                <MenuItem value="AT_RISK">AT_RISK</MenuItem>
                <MenuItem value="COMPLETED">COMPLETED</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Portfolio" value={objectiveForm.portfolioId} onChange={(e) => setObjectiveForm((v) => ({ ...v, portfolioId: e.target.value }))} fullWidth>
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {portfolios.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Project" value={objectiveForm.projectId} onChange={(e) => setObjectiveForm((v) => ({ ...v, projectId: e.target.value }))} fullWidth>
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField type="date" label="Start" value={objectiveForm.startDate} onChange={(e) => setObjectiveForm((v) => ({ ...v, startDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField type="date" label="End" value={objectiveForm.endDate} onChange={(e) => setObjectiveForm((v) => ({ ...v, endDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Unit" value={objectiveForm.unit} onChange={(e) => setObjectiveForm((v) => ({ ...v, unit: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Target" value={objectiveForm.targetValue} onChange={(e) => setObjectiveForm((v) => ({ ...v, targetValue: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Current" value={objectiveForm.currentValue} onChange={(e) => setObjectiveForm((v) => ({ ...v, currentValue: e.target.value }))} fullWidth />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogContent sx={{ pt: 0 }}>
          <DialogActions sx={{ px: 0, pb: 1 }}>
            <Button onClick={() => setEditOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>
              Cancel
            </Button>
            <Button onClick={saveObjective} variant="contained" disabled={saving || !objectiveForm.title.trim()} sx={{ borderRadius: 2 }}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      <Dialog open={krOpen} onClose={() => setKrOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>New key result</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
            <Grid item xs={12}>
              <TextField label="Title" value={krForm.title} onChange={(e) => setKrForm((v) => ({ ...v, title: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description" value={krForm.description} onChange={(e) => setKrForm((v) => ({ ...v, description: e.target.value }))} fullWidth multiline minRows={3} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Unit" value={krForm.unit} onChange={(e) => setKrForm((v) => ({ ...v, unit: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Target" value={krForm.targetValue} onChange={(e) => setKrForm((v) => ({ ...v, targetValue: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Current" value={krForm.currentValue} onChange={(e) => setKrForm((v) => ({ ...v, currentValue: e.target.value }))} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Direction"
                value={(krForm as any).direction || 'increase'}
                onChange={(e) => setKrForm((v: any) => ({ ...v, direction: e.target.value }))}
                fullWidth
              >
                <MenuItem value="increase">Increase (higher is better)</MenuItem>
                <MenuItem value="decrease">Decrease (lower is better)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Baseline (start value)"
                value={(krForm as any).startValue || ''}
                onChange={(e) => setKrForm((v: any) => ({ ...v, startValue: e.target.value }))}
                fullWidth
                helperText={
                  (krForm as any).direction === 'decrease'
                    ? 'Optional. If blank, we use Current as the starting point.'
                    : 'Used only for decrease-type key results.'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField type="date" label="Start" value={krForm.startDate} onChange={(e) => setKrForm((v) => ({ ...v, startDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField type="date" label="End" value={krForm.endDate} onChange={(e) => setKrForm((v) => ({ ...v, endDate: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setKrOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button onClick={createKr} variant="contained" disabled={savingKr || !krForm.title.trim()} sx={{ borderRadius: 2 }}>
            {savingKr ? 'Saving…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </PageLoader>
  );
};

export default OkrsPage;


